import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal, SectionLabel } from "@/components/Reveal";
import SectionBg from "@/components/SectionBg";
import { scrollToId } from "@/lib/attribution";
import { TICKER_ITEMS, FALSE_BELIEFS, REVELATION, AUDIENCE_CARDS, ROADMAP, HERO, HOST, ASSETS } from "@/data/content";

gsap.registerPlugin(ScrollTrigger);

export const Ticker = () => (
  <section className="border-y border-white/10 py-4 overflow-hidden" data-testid="proof-ticker">
    <div className="marquee font-mono text-[11px] tracking-[0.2em] text-white/40">
      {[0, 1].map((n) => (
        <div className="marquee-track" key={n} aria-hidden={n === 1}>
          {TICKER_ITEMS.map((t) => (
            <span key={t} className="mx-6 whitespace-nowrap">{t} <span className="text-[#D9622B] mx-6">◆</span></span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

const MANIFESTO_LINES = [
  { text: "VA LOAN ELIGIBILITY AND TRUE OUT-OF-POCKET COSTS", cls: "text-[#F0ECE4]" },
  { text: "SHOULD NOT FEEL", cls: "text-white/[0.28]" },
  { text: "LIKE DECODING", cls: "text-[#F0ECE4]" },
  { text: "FINE PRINT.", cls: "text-[#D9622B]" },
];

export const Manifesto = () => {
  const ref = useRef(null);
  useEffect(() => {
    const words = ref.current.querySelectorAll(".m-word");
    const tween = gsap.fromTo(words, { opacity: 0, y: 24 }, {
      opacity: 1, y: 0, stagger: 0.04, duration: 0.6, ease: "power3.out",
      scrollTrigger: { trigger: ref.current, start: "top 75%" },
    });
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);

  return (
    <section className="relative px-6 sm:px-12 lg:px-16 py-28 lg:py-40" data-testid="manifesto-section">
      <SectionBg src={ASSETS.bg.veteranKeys} opacity={0.15} position="center 30%" />
      <div className="relative z-10">
      <div className="absolute right-8 top-24 hidden lg:grid grid-cols-8 gap-2 opacity-100 pointer-events-none">
        {Array.from({ length: 64 }).map((_, i) => (
          <span key={i} className="w-2.5 h-2.5" style={{ background: "rgba(217,98,43,0.06)" }} />
        ))}
      </div>
      <SectionLabel num="001" text="THE PROBLEM" />
      <h2 ref={ref} className="mt-8 manifesto-h max-w-5xl">
        {MANIFESTO_LINES.map((l) => (
          <span key={l.text} className={`block ${l.cls}`}>
            {l.text.split(" ").map((w, i) => (
              <span key={i} className="m-word inline-block mr-[0.28em]">{w}</span>
            ))}
          </span>
        ))}
      </h2>
      <Reveal delay={0.15}>
        <p className="mt-10 text-white/55 text-base leading-[1.75] max-w-[560px]">
          For veterans and active-duty buyers in GA, FL, NC and TN, the VA benefit gets explained in fragments — a rate quote here, an acronym there, and nobody connecting what the guarantee actually covers to what you'll pay out of pocket. This 45-minute review connects it all: eligibility, reserves, credit, and a clear next step, in plain language from someone who's been on your side of the table.
        </p>
        <button data-testid="manifesto-cta" onClick={() => scrollToId("register")} className="mt-8 px-7 py-3.5 btn-outline border border-[#D9622B] text-[#D9622B] font-bold text-sm tracking-[0.1em]">
          RESERVE MY FREE SEAT →
        </button>
      </Reveal>
      </div>
    </section>
  );
};

export const Letter = () => (
  <section className="px-6 sm:px-12 py-28" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="letter-section">
    <div className="max-w-[760px] mx-auto">
      <SectionLabel num="002" text="A PERSONAL LETTER" />
      <Reveal><p className="mt-8 text-xl text-white/60 italic">{HERO.salutation}</p></Reveal>
      <div className="mt-8 space-y-4">
        {FALSE_BELIEFS.map((b, i) => (
          <Reveal key={b} delay={i * 0.08}>
            <div data-testid={`false-belief-${i + 1}`} className="lift-card flex gap-4 p-5 border" style={{ background: "rgba(255,0,0,0.04)", borderColor: "rgba(255,0,0,0.1)" }}>
              <span className="text-red-500 font-bold text-lg leading-none">✗</span>
              <p className="text-white/75 text-[15px] leading-relaxed">{b}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal><div className="my-12 h-px w-24 bg-[#D9622B]" /></Reveal>
      <div className="space-y-6">
        {REVELATION.map((p, i) => (
          <Reveal key={i} delay={i * 0.06}><p className="text-white/60 text-[15px] leading-[1.8]">{p}</p></Reveal>
        ))}
      </div>
      <Reveal><p className="mt-10 text-[#D9622B] font-bold">— {HOST.name}, {HOST.company}</p></Reveal>
    </div>
  </section>
);

export const Audience = () => (
  <section id="learn" className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="audience-section">
    <SectionBg src={ASSETS.bg.luxHomeEstate} opacity={0.14} />
    <div className="relative z-10">
    <SectionLabel num="003" text="WHO THIS IS FOR" />
    <Reveal><h2 className="mt-6 section-h">THIS TRAINING IS DESIGNED FOR:</h2></Reveal>
    <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4">
      {AUDIENCE_CARDS.map((c, i) => (
        <Reveal key={c.text} delay={i * 0.06}>
          <div data-testid={`audience-card-${i + 1}`} className="audience-card h-full p-6 lg:p-8 border border-white/10 bg-white/[0.02]">
            <span className="text-3xl">{c.icon}</span>
            <p className="mt-4 font-bold text-[15px] leading-snug text-[#F0ECE4]">{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <button data-testid="audience-cta" onClick={() => scrollToId("register")} className="mt-12 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
        ✓ RESERVE MY FREE SEAT
      </button>
    </Reveal>
    </div>
  </section>
);

export const Roadmap = () => (
  <section className="border-y border-white/10" data-testid="roadmap-section">
    <div className="grid grid-cols-2 lg:grid-cols-4">
      {ROADMAP.map((s, i) => (
        <div key={s.num} className={`lift-card relative p-8 lg:p-10 overflow-hidden ${i > 0 ? "border-l border-white/10" : ""} ${i > 1 ? "border-t lg:border-t-0 border-white/10" : ""}`} data-testid={`roadmap-step-${i + 1}`}>
          <span className="absolute -top-4 -right-2 font-mono text-[120px] font-bold leading-none text-white/[0.04] select-none">{s.num}</span>
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#D9622B]">{s.label}</p>
          <h3 className="mt-3 text-2xl font-black uppercase" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{s.name}</h3>
          <p className="mt-3 text-[13px] text-white/45 leading-relaxed">{s.desc}</p>
        </div>
      ))}
    </div>
  </section>
);
